import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Slide } from '../../types/contentSlicing';
import SlideCard from './SlideCard';

interface CoverFlowViewProps {
  slides: Slide[];
  text: string;
  vertical?: boolean;
}

function CoverFlowView({ slides, text, vertical = false }: CoverFlowViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastSlideRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest slide
  useEffect(() => {
    if (slides.length > 0 && lastSlideRef.current) {
      // Use setTimeout to ensure DOM has updated
      const timer = setTimeout(() => {
        lastSlideRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: vertical ? 'end' : 'nearest',
          inline: vertical ? 'nearest' : 'end',
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [slides.length, vertical]);

  if (slides.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No slides accepted yet. Generate your first slide to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRef}
      sx={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: 3,
        overflowX: vertical ? 'hidden' : 'auto',
        overflowY: vertical ? 'auto' : 'hidden',
        py: 3,
        px: 2,
        perspective: '1000px',
        scrollbarWidth: 'thin',
        width: '100%',
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
          height: vertical ? undefined : 8,
          width: vertical ? 8 : undefined,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: 4,
        },
      }}
    >
      {slides.map((slide, index) => {
        const isLastSlide = index === slides.length - 1;
        return (
          <Box
            key={index}
            ref={isLastSlide ? lastSlideRef : null}
            sx={{
              flexShrink: 0,
              transition: 'transform 0.3s ease',
            }}
          >
            <SlideCard slide={slide} slideNumber={index + 1} text={text} />
          </Box>
        );
      })}
    </Box>
  );
}

export default CoverFlowView;
